import React, { useEffect, useState } from "react";
import FormatUrl from "../../../../../../utils/UrlFormatter";
import moment from "moment";
import { toast } from "react-toastify";
import '../styles/SlotGrid.css';

const SlotGrid = ({ clinicId, practitionerId, selectedDate, onResetSelections }) => {
  const [slotsByStation, setSlotsByStation] = useState({});
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!practitionerId || !selectedDate) return;

    const fetchSlots = async () => {
      setLoading(true);
      setNoData(false);
      setSelectedSlots([]);

      try {
        const csrfRes = await fetch(FormatUrl("/csrf-token/"), {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        const { csrfToken } = await csrfRes.json();

        const url = FormatUrl(
          `/get-availability-slots-for-mark-ua/?clinic_id=${clinicId}&practitioner_id=${practitionerId}`
        );

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          credentials: "include",
        });

        const data = await res.json();

        if (data?.status === "success" && Array.isArray(data.slots)) {
          const stations = {};
          let grouped = {};

          // Case 1: Single-station response as array of arrays
          if (Array.isArray(data.slots[0])) {
            grouped["station_1"] = data.slots[0];
          } else {
            // Case 2: Merged/multi-station flattened response
            data.slots.forEach((slot) => {
              const stationNum = parseInt(slot.station_number, 10);
              const stationKey = isNaN(stationNum) ? "station_1" : `station_${stationNum}`;
              if (!grouped[stationKey]) grouped[stationKey] = [];
              grouped[stationKey].push(slot);
            });
          }

          Object.entries(grouped).forEach(([stationKey, slotList]) => {
            const filtered = slotList.filter((slot) => {
              const isAvailable = slot.status === "AV";
              const isSameDate =
                moment(slot.slot_start_time).format("YYYY-MM-DD") === selectedDate;
              const isFuture = moment(slot.slot_end_time).isAfter(moment());
              return isAvailable && isSameDate && isFuture;
            });

            stations[stationKey] = filtered.map((slot) => ({
              id: String(slot.id),
              label: `${formatTime(slot.slot_start_time)} - ${formatTime(slot.slot_end_time)}`,
              raw: slot,
            }));
          });

          setSlotsByStation(stations);
          setNoData(Object.values(stations).every((list) => list.length === 0));
        } else {
          setNoData(true);
        }
      } catch (err) {
        console.error("Error fetching slots:", err);
        setNoData(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSlots();
  }, [clinicId, practitionerId, selectedDate]);

  const formatTime = (isoStr) => moment(isoStr).format("h:mm A");

  const toggleSlotSelection = (slotId) => {
    setSelectedSlots((prev) =>
      prev.includes(slotId) ? prev.filter((id) => id !== slotId) : [...prev, slotId]
    );
  };

  const handleMarkUnavailable = async () => {
    if (!selectedSlots.length) return;
    setSubmitting(true);

    try {
      const csrfRes = await fetch(FormatUrl("/csrf-token/"), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const { csrfToken } = await csrfRes.json();

      const formData = new FormData();
      formData.append("clinic_id", clinicId);
      formData.append("practitioner_id", practitionerId);
      formData.append("slots", JSON.stringify(selectedSlots));

      const res = await fetch(FormatUrl("/make-slots-unavailable/"), {
        method: "POST",
        headers: {
          "X-CSRFToken": csrfToken,
        },
        credentials: "include",
        body: formData,
      });

      const data = await res.json();
      if (data?.status === "success") {
        toast.success("Slots marked as unavailable.");

        setSlotsByStation((prev) => {
          const updated = {};
          Object.entries(prev).forEach(([stationKey, slotList]) => {
            updated[stationKey] = slotList.filter(
              (slot) => !selectedSlots.includes(slot.id)
            );
          });
          return updated;
        });

        setSelectedSlots([]);
        onResetSelections?.();
      } else {
        toast.error(data?.error || "Something went wrong.");
      }
    } catch (err) {
      console.error("Failed to mark slots unavailable:", err);
      toast.error("Error occurred. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearSelection = () => setSelectedSlots([]);

  const firstStationKey = Object.keys(slotsByStation)[0];

  return (
    <div className="slot-row_manage_slots_settings">
      {(Object.keys(slotsByStation).length > 0 || loading) && (
        <label className="slot-label_manage_slots_settings">Available Slots</label>
      )}

      {loading && <p className="slot-message_manage_slots_settings">Loading slots...</p>}
      {noData && <p className="slot-message_manage_slots_settings">No slots for selected date.</p>}

      {!loading && !noData && firstStationKey && (
        <ul className="slot-list_manage_slots_settings">
          {(slotsByStation[firstStationKey] || []).map((slot) => (
            <li
              key={slot.id}
              className={`slot-item_manage_slots_settings ${
                selectedSlots.includes(slot.id) ? "selected" : ""
              }`}
              onClick={() => toggleSlotSelection(slot.id)}
            >
              {slot.label}
            </li>
          ))}
        </ul>
      )}

      {!loading && selectedDate && (
        <div className="slot-actions_manage_slots_settings">
          <button
            className="mark-unavailable-btn_manage_slots_settings"
            disabled={!selectedSlots.length || submitting}
            onClick={handleMarkUnavailable}
          >
            Mark Selected as Unavailable
          </button>
          {selectedSlots.length > 0 && (
            <button
              className="clear-btn_manage_slots_settings"
              onClick={handleClearSelection}
            >
              Clear Selection
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SlotGrid;
